import React, { useId } from 'react';
import { FaChevronDown, FaCrosshairs } from 'react-icons/fa';
import ClipTimestampField from './ClipTimestampField';

function HintTimeRow({
  label,
  kind,
  field,
  quote,
  timeValue,
  pairedValue,
  clipIndex,
  activeField,
  onActivateField,
  onSaveField,
  onCancelField,
  onSeek,
  onNudgeTime,
}) {
  const canEdit = clipIndex != null && onActivateField;
  const isActive = canEdit
    && activeField?.clipIndex === clipIndex
    && activeField?.field === field;

  return (
    <div className="clip-hint-drawer__anchor-block">
      {canEdit ? (
        <ClipTimestampField
          label={label}
          kind={kind}
          field={field}
          value={timeValue}
          pairedValue={pairedValue}
          isActive={isActive}
          previewTime={isActive ? activeField?.previewTime : null}
          onActivate={() => onActivateField(clipIndex, field)}
          onSave={onSaveField}
          onCancel={onCancelField}
          onSeek={onSeek}
          onNudge={(f, delta) => onNudgeTime?.(clipIndex, f, delta)}
        />
      ) : timeValue ? (
        <>
          <span className="clip-hint-drawer__anchor-label">{label}</span>
          <button
            type="button"
            className="clip-hint-drawer__seek"
            onClick={() => onSeek?.(timeValue)}
            title={`Seek to ${timeValue}`}
          >
            <FaCrosshairs aria-hidden />
            <span>{timeValue}</span>
          </button>
        </>
      ) : null}
      {quote && (
        <blockquote className="clip-hint-drawer__quote">{quote}</blockquote>
      )}
    </div>
  );
}

/**
 * Expandable cut-hint panel (clip card or timeline). Chevron down collapses with CSS transition.
 */
export default function ClipHintDrawer({
  open,
  onClose,
  cutHint,
  tag = '',
  timelineCuts = [],
  clipIndex = null,
  clipStartTime = '',
  clipEndTime = '',
  activeField = null,
  onActivateField,
  onSaveField,
  onCancelField,
  onSeek,
  onNudgeTime,
}) {
  const panelId = useId();
  if (!cutHint) return null;

  const displayTag = tag || cutHint.tag || 'clip';
  const inTime = clipStartTime || cutHint.anchors?.in?.time || cutHint.range?.start || '';
  const outTime = clipEndTime || cutHint.anchors?.out?.time || cutHint.range?.end || '';

  return (
    <div
      className={`clip-hint-drawer ${open ? 'clip-hint-drawer--open' : ''}`}
      aria-hidden={!open}
    >
      <div className="clip-hint-drawer__collapse">
        <div
          id={panelId}
          className="clip-hint-drawer__panel"
          role="region"
          aria-label={`Cut hint for ${displayTag}`}
        >
          <div className="clip-hint-drawer__toolbar">
            <span className="clip-hint-drawer__toolbar-title">Cut hint · {displayTag}</span>
            <button
              type="button"
              className="clip-hint-drawer__close"
              onClick={onClose}
              aria-expanded={open}
              aria-controls={panelId}
              title="Collapse hint"
            >
              <FaChevronDown aria-hidden />
            </button>
          </div>

          <HintTimeRow
            label="In"
            kind="start"
            field="startTime"
            quote={cutHint.anchors?.in?.quote}
            timeValue={inTime}
            pairedValue={outTime}
            clipIndex={clipIndex}
            activeField={activeField}
            onActivateField={onActivateField}
            onSaveField={onSaveField}
            onCancelField={onCancelField}
            onSeek={onSeek}
            onNudgeTime={onNudgeTime}
          />

          <HintTimeRow
            label="Out"
            kind="end"
            field="endTime"
            quote={cutHint.anchors?.out?.quote}
            timeValue={outTime}
            pairedValue={inTime}
            clipIndex={clipIndex}
            activeField={activeField}
            onActivateField={onActivateField}
            onSaveField={onSaveField}
            onCancelField={onCancelField}
            onSeek={onSeek}
            onNudgeTime={onNudgeTime}
          />

          {cutHint.verifyOut?.expectedLastLine && (
            <p className="clip-hint-drawer__verify">
              <span>Expected at out:</span> {cutHint.verifyOut.expectedLastLine}
            </p>
          )}

          {timelineCuts.length > 0 && (
            <div className="clip-hint-drawer__cuts">
              <h4>Timeline cut</h4>
              {timelineCuts.map((cut, i) => (
                <div key={`${cut.afterTag}-${i}`} className="clip-hint-drawer__cut-row">
                  {cut.cutAt?.time && onSeek && (
                    <button
                      type="button"
                      className="clip-hint-drawer__seek"
                      onClick={() => onSeek(cut.cutAt.time)}
                    >
                      <FaCrosshairs aria-hidden />
                      <span>{cut.cutAt.time}</span>
                    </button>
                  )}
                  {cut.cutAt?.quote && (
                    <span className="clip-hint-drawer__cut-quote">{cut.cutAt.quote}</span>
                  )}
                  {cut.bridgePreview && (
                    <p className="clip-hint-drawer__bridge">{cut.bridgePreview}</p>
                  )}
                  {cut.adjustNote && (
                    <p className="clip-hint-drawer__note">{cut.adjustNote}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
